import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNav';
import TopBar from '../components/TopBar';
import { useTranslation } from 'react-i18next';
import { trackPageView, trackClick, trackNavigation } from '../utils/analytics';

interface AccuracyState {
  overall?: number;
  pronunciation?: number;
  fluency?: number;
  tone?: number;
  rabbiWave?: number[];
  userWave?: number[];
  missedWords?: string[];
}

const AccuracyResultPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    trackPageView('AccuracyResult');
  }, []);

  const {
    overall = 0,
    pronunciation = 0,
    fluency = 0,
    tone = 0,
    rabbiWave = [],
    userWave = [],
    missedWords = [],
  } = (location.state as AccuracyState) || {};

  const hasData = location.state && typeof overall === 'number';
  let overallScore = overall;
  if (overallScore > 90) {
    overallScore = 90;
  }
  const radius = 90;
  const stroke = 14;
  const norm = radius - stroke / 2;
  const circ = 2 * Math.PI * norm;
  const prog = (overallScore / 100) * circ;

  const bar = (label: string, he: string, value: number, icon: string) => (
    <div className="mb-2">
      <div className="flex items-center justify-between text-[11px] font-semibold mb-1">
        <span className="flex items-center gap-1">
          {icon} {label} / {he}
        </span>
        <span>{Math.round(value / 10)}</span>
      </div>
      <div className="h-3 rounded-full bg-[#f1efe8] overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#0d6b3a] via-[#0d6b3a] to-[#caa42e]"
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );

  const wave = (data: number[], color: string) => (
    <div className="flex items-end h-20 overflow-hidden">
      {data.map((v, i) => (
        <div
          key={i}
          style={{ height: `${v * 100}%` }}
          className={`w-[4px] mx-[1px] rounded-sm ${color}`}
        />
      ))}
    </div>
  );

  if (!hasData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#f6f5f1] px-6">
        <div className="max-w-[420px] text-center">
          <h1 className="text-xl font-semibold mb-2 text-[#132a49]">No Results</h1>
          <p className="text-sm text-gray-600 mb-4">
            There is no accuracy data to display. Please record and compare a verse first.
          </p>
          <button
            onClick={() => navigate('/practice')}
            className="bg-[#132a49] text-white rounded-xl px-4 py-2 text-sm font-medium"
          >
            Go to Practice
          </button>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }} className="flex flex-col">
      <TopBar title={t('accuracy_title')} onBack={() => navigate(-1)} />
      <div className="w-full max-w-[980px] mx-auto overflow-y-auto" style={{ flexGrow: 1 }}>
        <div className="bg-white px-4 pt-4 pb-6 rounded-b-3xl shadow-lg">
          {/* Ring */}
          <div className="flex justify-center mb-4">
            <div className="relative w-[220px] h-[220px]">
              <svg
                viewBox={`0 0 ${radius * 2} ${radius * 2}`}
                className="rotate-[-90deg] w-full h-full"
              >
                <defs>
                  <linearGradient id="gradRing" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#caa42e" />
                    <stop offset="100%" stopColor="#18a058" />
                  </linearGradient>
                </defs>
                <circle
                  cx={radius}
                  cy={radius}
                  r={norm}
                  stroke="#e6e2d3"
                  strokeWidth={stroke}
                  fill="none"
                />
                <circle
                  cx={radius}
                  cy={radius}
                  r={norm}
                  stroke="url(#gradRing)"
                  strokeWidth={stroke}
                  strokeLinecap="round"
                  strokeDasharray={`${prog} ${circ - prog}`}
                  fill="none"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center rotate-0">
                <div className="text-5xl font-semibold text-[#132a49]">
                  {Math.round(overallScore / 10)}/10
                </div>
              </div>
            </div>
          </div>
          <div className="text-center mb-1 font-semibold text-[#132a49] text-sm">
            {overallScore >= 85
              ? 'Excellent!'
              : overallScore >= 70
                ? 'Great Job!'
                : 'Keep Practicing!'}
          </div>
          <div className="text-center text-[11px] text-gray-600 mb-4">
            {overallScore >= 85
              ? "You're making great progress"
              : 'Focus on consistency and pacing'}
          </div>

          {/* Detailed */}
          <div className="mb-3">
            <div className="flex items-center gap-1 text-[11px] font-semibold text-[#132a49] mb-2">
              ⚡ Detailed Analysis
            </div>
            {bar('Pronunciation', 'הגייה', pronunciation, '🗣️')}
            {bar('Fluency', 'שטף', fluency, '⚡')}
            {bar('Tone', 'טון', tone, '🎵')}
          </div>

          <div className="mb-3">
            <div className="text-[11px] font-semibold mb-2 flex items-center gap-1">
              🎙️ Voice Comparison
            </div>
            <div className="rounded-xl bg-[#f6f5f1] p-2 mb-2">
              <div className="text-[10px] mb-1 font-medium text-[#132a49]">• Rabbi's Recording</div>
              {wave(rabbiWave, 'bg-[#132a49]')}
            </div>
            <div className="rounded-xl bg-[#f6f5f1] p-2">
              <div className="text-[10px] mb-1 font-medium text-[#132a49]">• Your Recording</div>
              {wave(userWave, 'bg-gradient-to-t from-[#caa42e] to-[#d93b04]')}
            </div>
          </div>

          <div className="rounded-xl bg-gradient-to-b from-[#fff] to-[#fcf9ef] border border-[#f1ead3] p-3 mb-4">
            <div className="text-[11px] font-semibold mb-2 flex items-center gap-1">
              ✨ AI Feedback
            </div>
            <div className="text-[11px] mb-1 font-semibold">Strengths</div>
            <ul className="text-[11px] text-[#132a49] list-disc ml-4 mb-2 space-y-1">
              <li>Excellent pronunciation of complex words</li>
              <li>Good rhythm and pacing</li>
            </ul>
            <div className="text-[11px] mb-1 font-semibold">Areas to improve</div>
            <ul className="text-[11px] text-[#132a49] list-disc ml-4 space-y-1">
              <li>Work on emphasis in longer phrases</li>
            </ul>
            {missedWords.length > 0 && (
              <div className="mt-3">
                <div className="text-[11px] font-semibold mb-1">Missed Words</div>
                <div className="flex flex-wrap gap-2">
                  {missedWords.map((w, i) => (
                    <span
                      key={i}
                      className="px-2 py-1 rounded-md bg-[#132a49] text-white text-[10px]"
                    >
                      {w}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => {
                trackClick('Practice More', 'AccuracyResult');
                trackNavigation('AccuracyResult', 'Practice', 'button');
                navigate('/practice');
              }}
              className="flex-1 bg-[#132a49] text-white rounded-xl py-3 text-sm font-medium hover:bg-[#0f2239]"
            >
              Practice More
            </button>
            <button
              onClick={() => {
                trackClick('Back', 'AccuracyResult');
                trackNavigation('AccuracyResult', 'Practice', 'back');
                navigate(-1);
              }}
              className="flex-1 bg-[#caa42e] text-[#132a49] rounded-xl py-3 text-sm font-medium hover:bg-[#b69320]"
            >
              Back
            </button>
          </div>
        </div>
      </div>
      <BottomNav />
    </div>
  );
};

export default AccuracyResultPage;
